import { useContext, useEffect, useRef } from "react";
// Removed Shopify imports
import axios from "axios";
import { useAuth } from "@/Context/AuthContext";
import { LoadingContext } from "@/Context/LoadingContext";
import { ToastContext } from "@/Context/ToastContext";
import { logger } from "@/Services/Logger/Index";
import { handleError, localStorage } from "@/Utils/Index";

const instance = axios.create({
  baseURL: "/apps/api/", // Replace with your actual Shopify app base URL
  headers: {
    "Content-Type": "application/json",
  },
});

export function setAuthHeader(accessToken) {
  try {
    instance.defaults.headers.common["authorization"] = `Bearer ${accessToken}`;
  } catch (e) {
    return undefined;
  }
}

export function setAdminAuthHeader() {
  try {
    instance.defaults.headers.common["authorizationAdmin"] = localStorage()?.getItem("adminAccessToken");
  } catch (e) {
    return undefined;
  }
}

export function setAdminPanelAuthHeader() {
  try {
    instance.defaults.headers.common["authorization"] = localStorage()?.getItem("authorizationAdminPanel");
  } catch (e) {
    return undefined;
  }
}
export function setAuthorizationHeader() {
  try {
    // Check for admin token in URL query params first (for admin access to resumes)
    const urlParams = new URLSearchParams(window.location.search);
    const adminToken = urlParams.get("adminToken");

    if (adminToken) {
      // Store admin token in sessionStorage so it persists across resume pages
      sessionStorage.setItem("adminResumeAccessToken", adminToken);
      instance.defaults.headers.common["authorization"] = adminToken;
      return;
    }

    // Check if we have an admin token stored in sessionStorage (for navigation within resume pages)
    const storedAdminToken = sessionStorage.getItem("adminResumeAccessToken");
    if (storedAdminToken && window.location.pathname.includes("/app/resume")) {
      // Use stored admin token for resume pages
      instance.defaults.headers.common["authorization"] = storedAdminToken;
      return;
    }

    // Otherwise use regular user token from localStorage
    const token = localStorage()?.getItem("authorization");
    instance.defaults.headers.common["authorization"] = token;
  } catch (e) {
    console.error("❌ Error setting authorization header:", e);
    return undefined;
  }
}

export function useAuthenticatedFetch() {
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { showToast } = useContext(ToastContext);
  const { logout } = useAuth();

  useEffect(() => {
    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const errorCode = error?.response?.data?.code || error?.response?.status;
        const errorMessage = error?.response?.data?.message;

        // Check if admin is accessing resume (don't redirect admins to login)
        const isAdminAccess = sessionStorage.getItem("adminResumeAccessToken");

        // Check if we're on an admin panel route
        const isAdminPanelRoute = window.location.pathname.includes("/admin");

        // Check if we're on login or signup page (don't redirect, let the page handle errors)
        const isAuthPage =
          window.location.pathname.includes("/login") ||
          window.location.pathname.includes("/signup") ||
          window.location.pathname.includes("/forgot-password");

        // Handle 401 - Not authenticated, redirect to login (unless admin access or on auth page)
        if (errorCode === 401 || errorMessage === "Token is not valid") {
          stopLoading();
          if (!isAdminAccess && !isAuthPage) {
            // Redirect to appropriate login page based on route
            if (isAdminPanelRoute) {
              // Clear admin panel token and redirect to admin login
              localStorage.removeItem("authorizationAdminPanel");
              window.location.href = "/app/admin/login";
            } else {
              logout();
              window.location.href = "/app/login";
            }
          }
          return Promise.reject(error);
        }

        // Handle 403 - Authenticated but no access to this resume, redirect to dashboard
        if (errorCode === 403 && errorMessage === "You do not have access to this resume") {
          stopLoading();
          if (!isAdminAccess) {
            showToast("You don't have access to this resume. Redirecting to dashboard...", true);
            window.location.href = "/app/dashboard";
          }
          return Promise.reject(error);
        }

        // Handle other 403 errors (invalid token) - unless admin access or on auth page
        if (errorCode === 403) {
          stopLoading();
          if (!isAdminAccess && !isAuthPage) {
            // Redirect to appropriate login page based on route
            if (isAdminPanelRoute) {
              // Clear admin panel token and redirect to admin login
              localStorage.removeItem("authorizationAdminPanel");
              window.location.href = "/app/admin/login";
            } else {
              logout();
              window.location.href = "/app/login";
            }
          }
          return Promise.reject(error);
        }

        // Handle 402 - Insufficient credits
        // Don't show toast - let CreditContext InsufficientCreditsModal handle it
        if (errorCode === 402 || errorCode === "INSUFFICIENT_CREDITS") {
          stopLoading();
          return Promise.reject(error);
        }

        // Show error toast for all other errors (except on auth pages where we show inline errors)
        if (!isAuthPage) {
          showToast(handleError(error), true);
        }
        stopLoading();
        return Promise.reject(error);
      }
    );
  }, [instance]);

  try {
    const isAdminRoute = window.location.pathname.includes("/admin");
    if (isAdminRoute) {
      return {
        get: async (url, showIsLoading = true) => {
          showIsLoading && startLoading();
          setAdminPanelAuthHeader();
          const response = await instance.get(url);
          showIsLoading && stopLoading();
          return response.data;
        },
        post: async (
          url,
          data,
          showIsLoading = true,
          headers = {
            "Content-Type": "application/json",
          }
        ) => {
          showIsLoading && startLoading();
          setAdminPanelAuthHeader();
          instance.defaults.headers = { ...instance.defaults.headers, ...headers };
          const response = await instance.post(url, data);
          showIsLoading && stopLoading();
          return response.data;
        },
        put: async (url, data, showIsLoading = true) => {
          showIsLoading && startLoading();
          setAdminPanelAuthHeader();
          const response = await instance.put(url, data);
          showIsLoading && stopLoading();
          return response.data;
        },
        delete: async (url, showIsLoading = true) => {
          showIsLoading && startLoading();
          setAdminPanelAuthHeader();
          const response = await instance.delete(url);
          showIsLoading && stopLoading();
          return response.data;
        },
      };
    } else {
      return {
        get: async (url, showIsLoading = true) => {
          showIsLoading && startLoading();
          setAuthorizationHeader();
          const response = await instance.get(url);
          showIsLoading && stopLoading();
          return response.data;
        },
        post: async (
          url,
          data,
          showIsLoading = true,
          headers = {
            "Content-Type": "application/json",
          }
        ) => {
          showIsLoading && startLoading();
          setAuthorizationHeader();
          instance.defaults.headers = { ...instance.defaults.headers, ...headers };
          const response = await instance.post(url, data);
          showIsLoading && stopLoading();
          return response.data;
        },
        put: async (url, data, showIsLoading = true) => {
          showIsLoading && startLoading();
          setAuthorizationHeader();
          const response = await instance.put(url, data);
          showIsLoading && stopLoading();
          return response.data;
        },
        delete: async (url, showIsLoading = true) => {
          showIsLoading && startLoading();
          setAuthorizationHeader();
          const response = await instance.delete(url);
          showIsLoading && stopLoading();
          return response.data;
        },
      };
    }
  } catch (error) {
    logger.error(error);
  }
}
